import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Download, CheckCircle, Clock, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardRechnungen = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/invoices/my-invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Fehler beim Laden der Rechnungen');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (invoiceId) => {
    try {
      setDownloadingId(invoiceId);
      const token = localStorage.getItem('token');
      
      // Get PDF URL from backend
      const response = await axios.get(
        `${API}/invoices/${invoiceId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data && response.data.pdf_url) {
        // Open PDF in new tab
        window.open(response.data.pdf_url, '_blank');
        toast.success('Rechnung wird geöffnet');
      } else {
        toast.error('PDF-URL nicht gefunden');
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      const errorMessage = error.response?.data?.detail || 'Fehler beim Herunterladen der Rechnung';
      toast.error(errorMessage);
    } finally {
      setDownloadingId(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'succeeded':
      case 'paid':
        return (
          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
            <CheckCircle className="w-3 h-3 mr-1" />
            Bezahlt
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">
            <Clock className="w-3 h-3 mr-1" />
            Ausstehend
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
            <XCircle className="w-3 h-3 mr-1" />
            Fehlgeschlagen
          </Badge>
        );
      default:
        return (
          <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100">
            {status || 'Unbekannt'}
          </Badge>
        );
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0040FF] mx-auto mb-4"></div>
          <p className="text-gray-600">Rechnungen werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Rechnungen</h1>
        <p className="text-gray-600">
          Übersicht aller Ihrer Rechnungen und Zahlungen
        </p>
      </div>

      {/* Invoices List */}
      {invoices.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center py-12">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg font-medium mb-2">
              Keine Rechnungen vorhanden
            </p>
            <p className="text-gray-500 text-sm">
              Sobald Sie eine Zahlung tätigen, erscheinen hier Ihre Rechnungen
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {invoices.map((invoice) => (
            <Card key={invoice.id}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-[#0040FF]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold text-gray-900">
                          {invoice.invoice_number}
                        </h3>
                        {getStatusBadge(invoice.status)}
                      </div>
                      <p className="text-sm text-gray-600">
                        {invoice.campaign_name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(invoice.date)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">
                        €{invoice.amount.toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {invoice.currency?.toUpperCase() || 'EUR'}
                      </p>
                    </div>
                    
                    <Button
                      onClick={() => handleDownload(invoice.id)}
                      disabled={
                        downloadingId === invoice.id || 
                        !['succeeded', 'paid'].includes(invoice.status?.toLowerCase())
                      }
                      className="bg-[#0040FF] hover:bg-[#0030CC]"
                    >
                      {downloadingId === invoice.id ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Lädt...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Herunterladen
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardRechnungen;
